/**
 * AssessmentDesignItemBanks.jsx — Phase 2A-4
 * Item Banks: ACTIVE/ARCHIVED (bank-level)
 * Items: DRAFT → SUBMITTED_FOR_REVIEW → APPROVED → PUBLISHED → RETIRED
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, X, ChevronLeft, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell, useIsFaculty, useIsReviewer, useIsDesigner } from "../components/assessment-design/AssessmentDesignShell";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { "Authorization": `Bearer ${localStorage.getItem("academiaos_token")}`, "Content-Type": "application/json" }; }
function apiError(e, fb = "Error") {
  const d = e?.detail || e?.message || fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  return String(d);
}

const ITEM_TYPES = ["MCQ","SHORT_ANSWER","ESSAY","NUMERICAL","FILL_BLANK","MATCHING","PRACTICAL"];
const DIFFICULTIES = ["EASY","INTERMEDIATE","HARD"];
const BLOOM_LEVELS = ["REMEMBER","UNDERSTAND","APPLY","ANALYZE","EVALUATE","CREATE"];
const EMPTY_BANK = { code: "", title: "", description: "", subject_domain: "" };
const EMPTY_ITEM = { item_bank_id: "", item_type: "MCQ", stem: "", difficulty: "INTERMEDIATE", bloom_level: "" };

export default function AssessmentDesignItemBanks() {
  const isFaculty = useIsFaculty();
  const isReviewer = useIsReviewer();
  const isDesigner = useIsDesigner();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBank, setSelectedBank] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showCreateBank, setShowCreateBank] = useState(false);
  const [bankForm, setBankForm] = useState(EMPTY_BANK);
  const [savingBank, setSavingBank] = useState(false);
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [savingItem, setSavingItem] = useState(false);
  const [actioning, setActioning] = useState(null);

  const loadBanks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/assessment/item-banks`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setBanks(await r.json());
    } catch (e) { toast.error("Failed to load item banks: " + apiError(e)); }
    finally { setLoading(false); }
  }, []);

  const loadItems = useCallback(async (bankId) => {
    setItemsLoading(true);
    try {
      const r = await fetch(`${API}/assessment/items?item_bank_id=${bankId}`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setItems(await r.json());
    } catch (e) { toast.error("Failed to load items: " + apiError(e)); }
    finally { setItemsLoading(false); }
  }, []);

  useEffect(() => { loadBanks(); }, [loadBanks]);

  const openBank = (bank) => {
    setSelectedBank(bank);
    setItemForm(f => ({ ...f, item_bank_id: bank.id }));
    loadItems(bank.id);
  };

  const handleCreateBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      const body = { code: bankForm.code, title: bankForm.title };
      if (bankForm.description) body.description = bankForm.description;
      if (bankForm.subject_domain) body.subject_domain = bankForm.subject_domain;
      const r = await fetch(`${API}/assessment/item-banks`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Item Bank "${data.title}" created`);
      setShowCreateBank(false);
      setBankForm(EMPTY_BANK);
      loadBanks();
    } catch (e) { toast.error("Create failed: " + apiError(e)); }
    finally { setSavingBank(false); }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    try {
      const body = {
        item_bank_id: selectedBank.id,
        item_type: itemForm.item_type,
        stem: itemForm.stem,
        difficulty: itemForm.difficulty,
      };
      if (itemForm.bloom_level) body.bloom_level = itemForm.bloom_level;
      const r = await fetch(`${API}/assessment/items`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success("Item created");
      setShowCreateItem(false);
      setItemForm(f => ({ ...f, stem: "", bloom_level: "" }));
      loadItems(selectedBank.id);
    } catch (e) { toast.error("Create item failed: " + apiError(e)); }
    finally { setSavingItem(false); }
  };

  const handleItemAction = async (item, action) => {
    setActioning(`${item.id}-${action}`);
    try {
      const r = await fetch(`${API}/assessment/items/${item.id}/${action}`, {
        method: "POST", headers: authH(),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Item ${action.replace(/-/g, " ")} done`);
      loadItems(selectedBank.id);
    } catch (e) { toast.error(`${action} failed: ` + apiError(e)); }
    finally { setActioning(null); }
  };

  // ── Bank detail view ───────────────────────────────────────────────────────
  if (selectedBank) {
    return (
      <AssessmentDesignShell title={selectedBank.title} breadcrumb="Item Banks">
        <div className="mb-6">
          <button onClick={() => { setSelectedBank(null); setItems([]); }}
            data-testid="itembank-back-btn"
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Item Banks
          </button>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-100">{selectedBank.title}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-400">{selectedBank.subject_domain || "General"}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 font-mono">{selectedBank.code}</p>
            </div>
            {isFaculty && (
              <button type="button" onClick={() => setShowCreateItem(true)} data-testid="item-create-button"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors">
                <Plus className="w-4 h-4" /> Add Item
              </button>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-slate-300">Items ({items.length})</h3>
          </div>
          {itemsLoading ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading items…</div>
          ) : items.length === 0 ? (
            <div data-testid="items-empty" className="p-8 text-center text-slate-500 text-sm">
              No items yet.{" "}
              {isFaculty && <button onClick={() => setShowCreateItem(true)} className="text-sky-400 hover:text-sky-300">Add an item →</button>}
            </div>
          ) : (
            <div data-testid="items-list">
              {items.map(item => (
                <div key={item.id} data-testid={`item-row-${item.id}`}
                  className="flex items-start gap-4 px-4 py-3 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <LifecycleBadge status={item.status} />
                      <span className="text-xs px-2 py-0.5 rounded border border-slate-700 text-slate-400">{item.item_type}</span>
                      <span className="text-xs text-slate-500">{item.difficulty}</span>
                      {item.bloom_level && <span className="text-xs text-slate-500">Bloom: {item.bloom_level}</span>}
                    </div>
                    <p className="text-sm text-slate-200 line-clamp-2">{item.stem}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    {isFaculty && item.status === "DRAFT" && (
                      <button onClick={() => handleItemAction(item, "submit-for-review")}
                        disabled={actioning === `${item.id}-submit-for-review`}
                        data-testid={`item-submit-btn-${item.id}`}
                        className="px-2.5 py-1 rounded text-xs bg-amber-900/40 hover:bg-amber-800/60 text-amber-300 border border-amber-700/50 disabled:opacity-50 transition-colors">
                        {actioning === `${item.id}-submit-for-review` ? "…" : "Submit for Review"}
                      </button>
                    )}
                    {isReviewer && item.status === "REVIEWED" && (
                      <button onClick={() => handleItemAction(item, "approve")}
                        disabled={actioning === `${item.id}-approve`}
                        data-testid={`item-approve-btn-${item.id}`}
                        className="px-2.5 py-1 rounded text-xs bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-700/50 disabled:opacity-50 transition-colors">
                        {actioning === `${item.id}-approve` ? "…" : "Approve"}
                      </button>
                    )}
                    {isDesigner && item.status === "APPROVED" && (
                      <button onClick={() => handleItemAction(item, "publish")}
                        disabled={actioning === `${item.id}-publish`}
                        data-testid={`item-publish-btn-${item.id}`}
                        className="px-2.5 py-1 rounded text-xs bg-sky-900/40 hover:bg-sky-800/60 text-sky-300 border border-sky-700/50 disabled:opacity-50 transition-colors">
                        {actioning === `${item.id}-publish` ? "…" : "Publish"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add item modal */}
        {showCreateItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div data-testid="item-create-modal" className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
                <h2 className="text-base font-semibold text-slate-100">Add Item</h2>
                <button type="button" onClick={() => setShowCreateItem(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X className="w-4 h-4" /></button>
              </div>
              <form onSubmit={handleCreateItem} className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Type *</label>
                    <select data-testid="item-type-select" value={itemForm.item_type}
                      onChange={e => setItemForm(f => ({ ...f, item_type: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                      {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Difficulty</label>
                    <select data-testid="item-difficulty-select" value={itemForm.difficulty}
                      onChange={e => setItemForm(f => ({ ...f, difficulty: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                      {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Bloom Level</label>
                    <select data-testid="item-bloom-select" value={itemForm.bloom_level}
                      onChange={e => setItemForm(f => ({ ...f, bloom_level: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
                      <option value="">None</option>
                      {BLOOM_LEVELS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Stem (Question) *</label>
                  <textarea data-testid="item-stem-input" value={itemForm.stem}
                    onChange={e => setItemForm(f => ({ ...f, stem: e.target.value }))}
                    required rows={4} placeholder="Enter the question or prompt…"
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreateItem(false)}
                    className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>                  <button type="submit" disabled={savingItem} data-testid="item-create-submit-btn"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                    {savingItem ? "Adding…" : "Add Item"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AssessmentDesignShell>
    );
  }

  // ── Banks list ─────────────────────────────────────────────────────────────
  return (
    <AssessmentDesignShell title="Item Banks" breadcrumb="Item Banks">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">Item banks hold assessment items (questions/tasks). Items follow their own lifecycle.</p>
        <div className="flex items-center gap-2">
          <button onClick={loadBanks} disabled={loading} data-testid="itembanks-refresh-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isFaculty && (
            <button type="button" onClick={() => setShowCreateBank(true)} data-testid="itembank-create-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors">
              <Plus className="w-4 h-4" /> New Item Bank
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-slate-800/40 animate-pulse" />)}</div>
      ) : banks.length === 0 ? (
        <div data-testid="itembanks-empty" className="text-center py-16 text-slate-500 text-sm">
          No item banks yet.
          {isFaculty && <button onClick={() => setShowCreateBank(true)} className="block mt-2 text-sky-400 hover:text-sky-300 mx-auto">Create first bank →</button>}
        </div>
      ) : (
        <div data-testid="itembanks-list" className="space-y-2">
          {banks.map(bank => (
            <button key={bank.id} data-testid={`itembank-row-${bank.id}`}
              onClick={() => openBank(bank)}
              className="w-full text-left flex items-center gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-sky-800 transition-colors group">
              <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-slate-400 group-hover:text-sky-400 transition-colors" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-slate-100 group-hover:text-sky-300 transition-colors">{bank.title}</span>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                  <span className="font-mono">{bank.code}</span>
                  {bank.subject_domain && <span>{bank.subject_domain}</span>}
                  {bank.status && <span className={bank.status === "ACTIVE" ? "text-emerald-400" : "text-red-400"}>{bank.status}</span>}
                </div>
              </div>
              <ChevronLeft className="w-4 h-4 text-slate-600 group-hover:text-sky-500 rotate-180 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {showCreateBank && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-testid="itembank-create-modal" className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-base font-semibold text-slate-100">Create Item Bank</h2>
              <button type="button" onClick={() => { setShowCreateBank(false); setBankForm(EMPTY_BANK); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleCreateBank} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Code *</label>
                <input data-testid="itembank-code-input" value={bankForm.code}
                  onChange={e => setBankForm(f => ({ ...f, code: e.target.value }))} required
                  placeholder="e.g. IB-MATH-2026"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                <input data-testid="itembank-title-input" value={bankForm.title}
                  onChange={e => setBankForm(f => ({ ...f, title: e.target.value }))} required
                  placeholder="Item Bank Title"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Subject Domain</label>
                <input data-testid="itembank-domain-input" value={bankForm.subject_domain}
                  onChange={e => setBankForm(f => ({ ...f, subject_domain: e.target.value }))}
                  placeholder="e.g. Mathematics, Engineering"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreateBank(false); setBankForm(EMPTY_BANK); }}
                  className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">Cancel</button>
                <button type="submit" disabled={savingBank} data-testid="itembank-create-submit-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                  {savingBank ? "Creating…" : "Create Bank"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AssessmentDesignShell>
  );
}
